import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MonthlyClose } from '@eriko/shared-types';
import {
  getReviewQueue,
  getReviewOrgs,
  getOrgTransactions,
  getOrgMonthlyCloses,
  getCloseOpenItems,
  getPlatformMetrics,
  getOrgDetail,
  deleteMonthlyClose,
  updateTransaction as apiUpdateTransaction,
  updateMonthlyCloseStatus,
} from '../api/review';
import type {
  ReviewQueueItem,
  ReviewOrg,
  OrgTransactionsResponse,
  OrgDetail,
} from '../api/review';
import { extendTrial } from '../api/orgs';

export type { ReviewQueueItem, ReviewOrg, OrgTransactionsResponse, OrgDetail };

// --- Query hooks ---

export function useReviewQueue() {
  return useQuery({
    queryKey: ['admin', 'review-queue'],
    queryFn: getReviewQueue,
  });
}

export function useReviewOrgs() {
  return useQuery({
    queryKey: ['admin', 'review-orgs'],
    queryFn: getReviewOrgs,
  });
}

export function useOrgTransactions(orgId: string, monthlyCloseId?: string) {
  return useQuery({
    queryKey: ['admin', 'org-transactions', orgId, monthlyCloseId],
    queryFn: () => getOrgTransactions(orgId, monthlyCloseId),
    enabled: !!orgId,
  });
}

export function useOrgMonthlyCloses(orgId: string) {
  return useQuery({
    queryKey: ['admin', 'org-monthly-closes', orgId],
    queryFn: () => getOrgMonthlyCloses(orgId),
    enabled: !!orgId,
  });
}

// Open items (unresolved flags + open blocking conversations) holding a close's
// sign-off, for the "N open items" badge on the review header (backlog A1).
export function useCloseOpenItems(closeId?: string) {
  return useQuery({
    queryKey: ['admin', 'close-open-items', closeId],
    queryFn: () => getCloseOpenItems(closeId!),
    enabled: !!closeId,
  });
}

// Aggregated platform metrics for the admin dashboard (backlog B2). Platform-admin
// only — the endpoint 403s for reviewers, so gate the nav link on userType too.
export function usePlatformMetrics(enabled = true) {
  return useQuery({
    queryKey: ['admin', 'platform-metrics'],
    queryFn: getPlatformMetrics,
    enabled,
  });
}

export function useOrgDetail(orgId: string) {
  return useQuery({
    queryKey: ['admin', 'org-detail', orgId],
    queryFn: () => getOrgDetail(orgId),
    enabled: !!orgId,
  });
}

export function useDeleteMonthlyClose() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, closeId }: { orgId: string; closeId: string }) =>
      deleteMonthlyClose(orgId, closeId),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'org-monthly-closes', vars.orgId],
      });
      void queryClient.invalidateQueries({ queryKey: ['admin-queue-summary'] });
    },
  });
}

// --- Mutation hooks ---

interface UpdateTransactionPayload {
  id: string;
  data: {
    categoryId?: string;
    isReviewed?: boolean;
    flags?: Array<{
      id: string;
      isResolved: boolean;
      resolution?: string;
    }>;
  };
}

export function useUpdateTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: UpdateTransactionPayload) => apiUpdateTransaction(id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'org-transactions'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'review-queue'] });
      // Resolving a flag changes the close's open-items count → refresh the badge.
      void queryClient.invalidateQueries({ queryKey: ['admin', 'close-open-items'] });
    },
  });
}

interface UpdateMonthlyClosePayload {
  id: string;
  data: {
    status: MonthlyClose['status'];
    // force: true bypasses the server-side sign-off guard (unresolved flags /
    // open blocking conversations) when a reviewer consciously signs off anyway.
    force?: boolean;
  };
}

export function useUpdateMonthlyClose() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: UpdateMonthlyClosePayload) => updateMonthlyCloseStatus(id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
  });
}

export function useExtendTrial(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { days: number; reason: string }) => extendTrial(orgId, params),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'org-detail', orgId] });
    },
  });
}
